import cx from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	isUnreadNotification,
	useUpozorneniContextInstance,
} from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	Dialog,
	ErrorStatusBanner,
	InfoBanner,
	Pagination,
	Icon,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { scrollToTop } from '@gov-nx/web';
import { ModalView } from './ModalView';
import { NotificationsSkeleton } from './NotificationsSkeleton';

export interface NotificationsFormProps {
	code: PageCode;
}

export const NotificationsView = ({ code }: NotificationsFormProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const {
		data,
		numberOfNotifications,
		goToPage,
		currentPage,
		filter,
		controls,
		categorizedData,
		selectedNotification,
	} = useUpozorneniContextInstance();

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	if (controls.initialLoading) {
		return <NotificationsSkeleton />;
	}

	if (!data || data.length === 0) {
		return (
			<ContentLayout>
				<InfoBanner
					headline={ts('stav.chyba.zatim-tu-nic-nemame')}
					text={ts('stav.chyba.nemate-zadne-upozorneni')}
				/>
			</ContentLayout>
		);
	}

	return (
		<ContentLayout>
			<ul className="gov-list--plain">
				{categorizedData.map((category) => (
					<li key={category.key}>
						<h2 className="my-2 text-body-xs! text-(--text-secondary) text-center">
							{category.label}
						</h2>
						<ul className="gov-list--plain mt-0!">
							{category.notifications.map((item) => {
								const isUnread = isUnreadNotification(item);
								return (
									<li
										key={item.id.toString()}
										className="-mx-5 border-b border-(--border-subtlest) [ last-of-type:border-b-0 ] [ md:mx-0 ]">
										<Link
											to="#"
											onClick={() => selectedNotification.select(item)}
											className="anim-bg-hover relative block pt-(--templates-margin-l) pb-(--templates-margin-l) no-underline!">
											<div
												className={cx(
													'flex gap-(--spacing-s-nudge)',
													!isUnread && 'pl-6'
												)}>
												{isUnread && (
													<Icon
														type="templates"
														name="notification"
														size="s"
														aria-label="nepřečteno"
														className="relative top-1 text-(--background-status-warning)"
													/>
												)}
												<h3
													className={cx(
														'pr-6 text-body-m! text-(--text-primary-color) [ md:max-w-[70%] ] [ xl:max-w-[70%] xl:pr-0 ]',
														isUnread && 'font-bold'
													)}>
													{item.nazev}
												</h3>
											</div>
											<time
												className={cx(
													'relative top-auto right-auto inline-block mt-2 pl-6 text-(--text-secondary)! [ md:absolute md:top-6 md:right-[34px] md:mt-0 ]',
													isUnread && 'font-bold'
												)}>
												{item.datumACasVytvoreni
													? toDateReadable(item.datumACasVytvoreni)
													: undefined}
											</time>
											<Icon
												type="components"
												name="chevron-right"
												size="l"
												className="absolute top-[20px] right-0 text-(--icon-on-subtle)) [ md:top-[27px] ]"
											/>
										</Link>
									</li>
								);
							})}
						</ul>
					</li>
				))}
			</ul>
			<Dialog
				isOpen={!!selectedNotification.id}
				onClose={selectedNotification.unselect}
				title={<h3>{selectedNotification.modalLabel}</h3>}>
				{selectedNotification.id ? (
					<ModalView
						selectedNotification={selectedNotification}
						code={code}>
						{selectedNotification.link?.url &&
						selectedNotification.link?.urlText ? (
							<Button
								type="solid"
								color="primary"
								size="m"
								iconStart={
									<Icon
										name="box-arrow-up-right"
										type="components"
									/>
								}
								asChild>
								<Link
									target="_blank"
									to={selectedNotification.link.url}>
									{selectedNotification.link.urlText}
								</Link>
							</Button>
						) : null}
					</ModalView>
				) : null}
			</Dialog>
			{numberOfNotifications > filter.pocet ? (
				<footer className="mt-6">
					<Pagination
						color="primary"
						size="m"
						onPage={(event) => {
							scrollToTop();
							goToPage(event);
						}}
						current={currentPage}
						pageSize={filter.pocet}
						total={numberOfNotifications}
					/>
				</footer>
			) : null}
		</ContentLayout>
	);
};
